import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TO_API, KIND_COLORS, KIND_LABELS } from '../TimeOff';

const pad = (n) => String(n).padStart(2, '0');
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

export default function WhosOff() {
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [data, setData] = useState(null);
  const month = monthKey(cursor);

  useEffect(() => {
    axios.get(`${TO_API}/whos-off`, { params: { month } }).then(r => setData(r.data)).catch(() => toast.error("Could not load who's off"));
  }, [month]);

  const count = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const lead = (cursor.getDay() + 6) % 7;
  const cells = [...Array(lead).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  const today = new Date().toISOString().slice(0, 10);
  const shift = (n) => setCursor(c => new Date(c.getFullYear(), c.getMonth() + n, 1));
  const totalOff = Object.values(data?.days || {}).reduce((s, l) => s + l.length, 0);

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-center flex-wrap">
        <button onClick={() => shift(-1)} className="p-2 rounded-lg bg-slate-900/5 text-slate-900" data-testid="to-whos-prev"><ChevronLeft className="w-4 h-4" /></button>
        <span className="text-slate-900 font-semibold text-lg" data-testid="to-whos-month">{cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
        <button onClick={() => shift(1)} className="p-2 rounded-lg bg-slate-900/5 text-slate-900" data-testid="to-whos-next"><ChevronRight className="w-4 h-4" /></button>
        <span className="text-sm text-slate-800 ml-auto" data-testid="to-whos-total">{totalOff} person-day{totalOff !== 1 ? 's' : ''} off this month</span>
      </div>
      <div className="flex gap-3 flex-wrap text-xs text-slate-800">
        {Object.entries(KIND_LABELS).map(([k, l]) => <span key={k} className="inline-flex items-center gap-1"><span className={`w-3 h-3 rounded ${KIND_COLORS[k].split(' ')[0]}`} />{l}</span>)}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-2 overflow-x-auto">
        <div className="grid grid-cols-7 gap-1 min-w-[700px]" data-testid="to-whos-grid">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => <span key={d} className="text-xs text-slate-500 font-semibold px-1">{d}</span>)}
          {cells.map((d, i) => {
            if (!d) return <span key={i} />;
            const key = `${month}-${pad(d)}`;
            const list = data?.days?.[key] || [];
            const stat = data?.stat?.[key];
            const weekend = (lead + d - 1) % 7 >= 5;
            return (
              <div key={i} data-testid={`to-whos-day-${key}`}
                className={`min-h-[84px] rounded-lg border p-1 ${key === today ? 'border-blue-500 ring-1 ring-blue-300' : 'border-slate-100'} ${weekend || stat ? 'bg-slate-50' : 'bg-white'}`}>
                <div className="flex justify-between items-start">
                  <span className={`text-xs font-semibold ${weekend ? 'text-slate-500' : 'text-slate-700'}`}>{d}</span>
                  {stat && <span className="text-[9px] px-1 rounded bg-indigo-100 text-indigo-800 truncate max-w-[70px]" title={stat}>{stat}</span>}
                </div>
                <div className="space-y-0.5 mt-1">
                  {list.map((p, j) => (
                    <div key={j} className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate ${KIND_COLORS[p.kind]}`} title={`${p.name} — ${KIND_LABELS[p.kind]}`}>{p.name}</div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
